import { useState, useCallback } from 'react';
import { AlertTriangle, Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import type { DefaultRoleOption } from '@/types/share-types';

/**
 * Generic role labels for the share modal's access list. "Discoverable" is
 * NOT the default: it means "visible in the list but not accessible", which
 * is true of workflow viewers only (see ROLE_OPTIONS below) and the
 * opposite of what a viewer of any other resource has. Resources that mean
 * it opt in via their config's `roleDisplayNames`.
 */
export const ROLE_DISPLAY_NAMES: Record<string, string> = {
  viewer: 'Viewer',
  editor: 'Editor',
  owner: 'Owner',
};

const ROLE_OPTIONS = [
  {
    value: 'workflow:viewer' as const,
    label: 'Discoverable',
    description: 'Visible in the workflow list, but not accessible',
  },
  {
    value: 'workflow:editor' as const,
    label: 'Editable',
    description: 'Can view, edit, run, and share this workflow',
  },
];

interface DefaultAccessDropdownProps {
  value: DefaultRoleOption;
  onChange: (role: DefaultRoleOption) => void;
  showClientDataWarning: boolean;
  showWarningBanner?: boolean;
  disabled?: boolean;
}

export function DefaultAccessDropdown({
  value,
  onChange,
  showClientDataWarning,
  showWarningBanner = showClientDataWarning,
  disabled,
}: DefaultAccessDropdownProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const currentOption = ROLE_OPTIONS.find((o) => o.value === value);
  const currentLabel = currentOption?.label ?? 'Discoverable';

  const handleSelect = useCallback(
    (role: DefaultRoleOption) => {
      if (
        showClientDataWarning &&
        role === 'workflow:editor' &&
        value !== 'workflow:editor'
      ) {
        setShowConfirmDialog(true);
        return;
      }
      onChange(role);
    },
    [showClientDataWarning, value, onChange],
  );

  const handleConfirm = useCallback(() => {
    setShowConfirmDialog(false);
    onChange('workflow:editor');
  }, [onChange]);

  return (
    <div className='flex flex-col gap-2'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='outline'
            size='sm'
            className='w-36 justify-between'
            disabled={disabled}
          >
            {currentLabel}
            <ChevronDown className='size-4 opacity-50' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-72'>
          {ROLE_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              className='flex flex-col items-start gap-0.5 py-2'
              onSelect={() => handleSelect(option.value)}
            >
              <div className='flex w-full items-center justify-between'>
                <span className='text-sm font-medium'>{option.label}</span>
                {value === option.value && (
                  <Check className='size-4 text-foreground' />
                )}
              </div>
              <span className='text-xs text-muted-foreground'>
                {option.description}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {showWarningBanner && value === 'workflow:editor' && (
        <div className='flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200'>
          <AlertTriangle className='size-4 shrink-0 mt-0.5' />
          <span>
            This workflow is accessible to all organization members. Ensure it
            does not contain client-specific or sensitive data.
          </span>
        </div>
      )}

      <Modal
        isDialogOpen={showConfirmDialog}
        setIsDialogOpen={setShowConfirmDialog}
        title='Change default access to Editable?'
        contentClassName='sm:max-w-md'
        footer={
          <div className='flex gap-2'>
            <Button
              variant='outline'
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </div>
        }
      >
        <div className='flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200'>
          <AlertTriangle className='size-5 shrink-0 mt-0.5' />
          <span>
            All organization members will be able to view, edit, run, and share
            this workflow. Ensure this workflow does not contain client-specific
            or sensitive data before proceeding.
          </span>
        </div>
      </Modal>
    </div>
  );
}
